import { resolveLanguage, type UiLanguage } from "@ai-guidance/shared";
import { OTHER_CHROME_COPY } from "./uiTextLocales";

/**
 * TOT textul pe care îl scriem NOI și îl citește vizitatorul: etichete de
 * butoane, `aria-label`-uri, placeholder-e, notele de eroare, salutul implicit.
 *
 * Regula, fără excepții: engleza nu e implicitul, e una dintre cele două limbi.
 * Ce ajunge în fața vizitatorului urmează `business.language` din configul
 * appului - la fel ca nudge-urile și ca textele de commerce. Pe 9 aug 2026 un
 * sit cu `language: "ro"` a primit salutul și fallback-ul în engleză: nu era o
 * scăpare de traducere, era engleza scrisă direct în DOM, fără drum spre limbă.
 *
 * `aria-label`-urile intră aici cu drepturi depline: un cititor de ecran le
 * ROSTEȘTE, deci sunt text vizitator, nu metadate.
 *
 * Ce NU intră: promptul personei (modelul vorbește limba vizitatorului, e
 * treaba lui) și numele de brand („Powered by AskMyWebsite[AI]"). Numele
 * implicit al asistentului din header stă în tabel (`assistantName`), dar e
 * „AI Concierge" în TOATE limbile: e numele produsului, nu o descriere de
 * tradus (decizia lui Radu, 31 oct 2026, aceeași cu poziționarea). Proprietarul
 * îl schimbă din dashboard dacă vrea alt titlu.
 *
 * Alegerea limbii e una singură pentru tot produsul: `resolveLanguage` din
 * `shared`, aceeași funcție pe care o folosește și backendul.
 */
export type { UiLanguage };

export const uiLanguage = resolveLanguage;

/**
 * Răspunsurile LOCALE ale comenzilor scrise din orb-chat (`/voice-2`, `/debug`
 * - vezi `orbchat/index.ts`). Ele nu pleacă spre server și nu intră în fir, dar
 * le citește un om, deci urmează limba afacerii ca orice alt text al nostru.
 *
 * Comenzile rămân NEDESCOPERIBILE, la cererea proprietarului: sunt unelte de
 * tester, nu funcții ale vizitatorului. Deci nimic nu le anunță - niciun
 * placeholder, niciun `aria-label`, nicio listă de ajutor.
 */
export interface CommandCopy {
  /** Confirmarea schimbării de voce. `{0}` = cifra variantei. */
  voiceSet: string;
  /** `{0}` = id-ul sesiunii, `{1}` = cifra vocii curente. */
  debug: string;
}

const COMMAND_COPY: Record<UiLanguage, CommandCopy> = {
  ro: {
    voiceSet: "Vocea {0} e activă - pornește conversația ca să o auzi.",
    debug: "Sesiune: {0} · voce: {1}",
  },
  en: {
    voiceSet: "Voice {0} is active - start the conversation to hear it.",
    debug: "Session: {0} · voice: {1}",
  },
};

export function commandCopy(lang: UiLanguage): CommandCopy {
  return COMMAND_COPY[lang];
}

export interface ChromeCopy {
  /** Numele implicit din headerul panoului, când proprietarul n-a pus unul. */
  assistantName: string;
  /* --- panoul clasic --- */
  close: string;
  open: string;
  placeholder: string;
  send: string;
  stop: string;
  mic: string;
  micListening: string;
  thinking: string;
  /* --- orb-chat --- */
  orbAsk: string;
  orbPlaceholder: string;
  historyLabel: string;
  historyShow: string;
  historyHide: string;
  /** Ce scrie în card cât timp răspunsul se compune. */
  thinkingLabel: string;
  /* --- voce (ambele suprafețe) --- */
  voiceStart: string;
  voiceEnd: string;
  /**
   * Aceleași două stări, în două cuvinte: eticheta VIZIBILĂ din pastila de voce
   * a orb-chatului, care de pe 12 aug 2026 stă în rândul de scris. Propoziția
   * întreagă ar fi lăsat câmpul un ciot pe telefon - ea rămâne pe `aria-label`,
   * deci cititorul de ecran aude tot.
   */
  voiceStartShort: string;
  voiceEndShort: string;
  /* --- note de dictare (nu replici ale asistentului, vezi widget.ts) --- */
  micDenied: string;
  /** Notiță deterministă la mascarea datelor sensibile (redactSensitive) - garanția vizuală, indiferent de model. */
  sensitiveRedacted: string;
  micNoSpeech: string;
  micNetwork: string;
  micFailed: string;
  /* --- note de voce --- */
  voiceNotConfigured: string;
  voiceMicBlocked: string;
  voiceInAppBrowser: string;
  voiceNoMic: string;
  voiceFailed: string;
  voiceBusy: string;
  /**
   * Reluarea automată a conversației vocale de după o navigare a fost blocată
   * de browser (Safari cere un gest pentru microfon). Sesiunea trăiește pe
   * server, deci nota îi spune vizitatorului exact gestul care o continuă.
   */
  voiceResumeTap: string;
  /**
   * Minutele de voce per vizitator s-au terminat (F-06): fie la epuizarea
   * în mijlocul conversației (după rămas-bunul rostit), fie la un start
   * refuzat cu 403. Continuarea e în scris, cu firul deja în chat.
   */
  voiceMinutesOver: string;
  /* --- mesaje în firul conversației --- */
  /**
   * Salutul implicit, cel de dinaintea oricărei personalizări din dashboard.
   * Varianta RO e cea validată în `docs/plan-voice-variants.md`: întreabă de
   * PROIECT, nu de produs - vizitatorul care încă nu știe ce caută are ce
   * răspunde.
   */
  greeting: string;
  askFailed: string;
  targetMissing: string;
}

const CHROME_COPY: Record<UiLanguage, ChromeCopy> = {
  ro: {
    assistantName: "AI Concierge",
    close: "Închide asistentul",
    open: "Deschide asistentul",
    placeholder: "Întreabă-mă ceva…",
    send: "Trimite",
    stop: "Oprește răspunsul",
    mic: "Dictează-ți mesajul",
    micListening: "Te ascult… apasă din nou ca să opresc",
    thinking: "Asistentul se gândește",
    orbAsk: "Întreabă situl",
    orbPlaceholder: "Întreabă-mă orice despre situl ăsta…",
    historyLabel: "Istoricul conversației",
    historyShow: "Arată conversația",
    historyHide: "Ascunde conversația",
    thinkingLabel: "… mă gândesc …",
    voiceStart: "Vorbește cu asistentul",
    voiceEnd: "Încheie conversația vocală",
    voiceStartShort: "Vorbește",
    voiceEndShort: "Oprește conversația",
    micDenied: "Accesul la microfon a fost refuzat. Permite-l din browser și încearcă din nou.",
    sensitiveRedacted: "Datele sensibile (card, email, parole) au fost eliminate automat înainte de trimitere - nu sunt niciodată necesare aici.",
    micNoSpeech: "N-am auzit nimic - verifică microfonul și încearcă din nou.",
    micNetwork: "Serviciul de dictare al browserului tău nu răspunde.",
    micFailed: "Dictarea s-a oprit pe neașteptate. Încearcă din nou.",
    voiceNotConfigured: "Vocea nu e disponibilă aici încă.",
    voiceMicBlocked:
      "Microfonul e blocat pentru situl ăsta. Permite-l din iconița din stânga barei de adrese, apoi reîncarcă pagina.",
    voiceNoMic: "Nu am găsit niciun microfon pe dispozitivul ăsta.",
    voiceFailed: "Conversația vocală nu a putut porni. Încearcă din nou.",
    voiceBusy: "Toate liniile vocale sunt ocupate acum. Scrie-mi aici între timp - răspund la fel de repede.",
    voiceResumeTap: "Conversația vocală e pe pauză - atinge microfonul ca să continuăm de unde am rămas.",
    voiceMinutesOver: "Minutele de conversație vocală s-au terminat - continuăm aici, în scris. Conversația noastră e deja în fir.",
    voiceInAppBrowser:
      "Fereastra din aplicație nu are acces la microfon. Deschide linkul în browserul telefonului (Safari sau Chrome) și conversația vocală va merge.",
    greeting: "Salut! Spune-mi ce proiect ai sau ce cauți, și te ajut eu.",
    askFailed: "Ceva n-a mers. Încearcă din nou.",
    targetMissing: "Nu găsesc elementul ăla pe pagina asta. Ești pe pagina potrivită?",
  },
  en: {
    assistantName: "AI Concierge",
    close: "Close the assistant",
    open: "Open the assistant",
    placeholder: "Ask something…",
    send: "Send",
    stop: "Stop the answer",
    mic: "Dictate your message",
    micListening: "Listening… click again to stop",
    thinking: "The assistant is thinking",
    orbAsk: "Ask this site",
    orbPlaceholder: "Ask anything about this site…",
    historyLabel: "Conversation history",
    historyShow: "Show the conversation",
    historyHide: "Hide the conversation",
    thinkingLabel: "… thinking …",
    voiceStart: "Talk to the assistant",
    voiceEnd: "End the voice conversation",
    voiceStartShort: "Talk",
    voiceEndShort: "Stop conversation",
    micDenied: "Microphone access was denied. Allow it in your browser and try again.",
    sensitiveRedacted: "Sensitive details (card numbers, emails, passwords) were removed automatically before sending - they are never needed here.",
    micNoSpeech: "I didn't hear anything - check your microphone and try again.",
    micNetwork: "Your browser's dictation service is not responding.",
    micFailed: "Dictation stopped unexpectedly. Try again.",
    voiceNotConfigured: "Voice is not available here yet.",
    voiceMicBlocked:
      "The microphone is blocked for this site. Allow it from the icon on the left of the address bar, then reload the page.",
    voiceNoMic: "No microphone was found on this device.",
    voiceFailed: "The voice conversation could not be started. Please try again.",
    voiceBusy: "All voice lines are busy right now. Type to me here in the meantime - I reply just as fast.",
    voiceResumeTap: "The voice conversation is paused - tap the microphone to pick up where we left off.",
    voiceMinutesOver: "The voice minutes are used up - let's continue right here in writing. Our conversation is already in the thread.",
    voiceInAppBrowser:
      "This in-app window has no microphone access. Open the link in your phone browser (Safari or Chrome) and voice will work.",
    greeting: "Hi! I can see what you're doing - tell me what you're looking for and I'll guide you.",
    askFailed: "Something went wrong. Please try again.",
    targetMissing: "I can't find that element on this page. Are you on the right page?",
  },
};

/** Limbile în care chrome-ul widgetului e tradus. Orice altă limbă cade pe engleză. */
export type ChromeLanguage = UiLanguage | keyof typeof OTHER_CHROME_COPY;

const ALL_CHROME_COPY: Record<ChromeLanguage, ChromeCopy> = { ...CHROME_COPY, ...OTHER_CHROME_COPY };

/** Nume de limbă (în engleză și în limba ei) -> cod, pentru `business.language` scris liber de scan. */
const LANGUAGE_NAMES: [RegExp, ChromeLanguage][] = [
  [/roman|român/, "ro"],
  [/spanish|espa[nñ]ol|castellano/, "es"],
  [/german|deutsch/, "de"],
  [/italian/, "it"],
  [/french|fran[cç]ais/, "fr"],
  [/portugu/, "pt"],
  [/english|engl[eé]s/, "en"],
];

/**
 * Un text de limbă („es", „es-ES", „Spanish", „español") -> limba de chrome, sau
 * `undefined` dacă nu o recunoaștem (limbă fără traducere, șir gol). Codul se
 * citește din PREFIX (`pt-BR` -> pt), numele dintr-o căutare în text.
 */
export function matchChromeLanguage(raw: string | undefined | null): ChromeLanguage | undefined {
  const value = (raw ?? "").trim().toLowerCase();
  if (!value) return undefined;
  const code = value.split(/[-_\s]/)[0];
  if (code in ALL_CHROME_COPY) return code as ChromeLanguage;
  for (const [pattern, lang] of LANGUAGE_NAMES) if (pattern.test(value)) return lang;
  return undefined;
}

/** Limba paginii: `<html lang>`, apoi limba browserului. */
function pageLanguage(): ChromeLanguage | undefined {
  if (typeof document === "undefined") return undefined;
  return (
    matchChromeLanguage(document.documentElement?.lang) ??
    matchChromeLanguage(typeof navigator === "undefined" ? undefined : navigator.language)
  );
}

/**
 * Limba chrome-ului widgetului. Ordinea:
 *   1. `business.language` din config, dacă o recunoaștem - proprietarul sau
 *      scanul au spus-o explicit, iar `<html lang>` e adesea lăsat pe „en" pe
 *      situri care nu sunt englezești;
 *   2. limba paginii (`<html lang>`), apoi a browserului - un sit fără limbă
 *      declarată nu mai primește automat engleză (regresia 30 oct 2026, D12a);
 *   3. engleza.
 * Un salut scris de proprietar (`branding.welcomeMessage`) nu trece pe aici deloc.
 */
export function chromeLanguage(businessLanguage: string | undefined): ChromeLanguage {
  return matchChromeLanguage(businessLanguage) ?? pageLanguage() ?? "en";
}

/** Copy-ul de chrome pentru limba appului. `language` e `business.language`, brut. */
export function chromeCopy(language: string | undefined): ChromeCopy {
  return ALL_CHROME_COPY[chromeLanguage(language)];
}
